export interface LiveHeatmapGradientStop {
  /** Raw heat value, normalized against `maxValue` when building the palette. */
  value: number;
  color: string;
}

export interface LiveHeatmapConfig {
  /** Raster resolution relative to the floor-plan image. */
  rasterScale: number;
  /** Physical radius of a tracked-person dot, equivalent to legacy `point_size_in_meters`. */
  pointRadiusInMeters: number;
  /** Heat core radius relative to the configured person-dot radius. */
  heatRadiusMultiplier: number;
  /** Legacy blur radius multiplier applied to the solid core radius. */
  blurFactor: number;
  /** Heat contribution assigned to each tracked object. */
  pointWeight: number;
  /** Legacy maximum used to normalize each object's heat contribution. */
  maxValue: number;
  /** Legacy lower bound for an individual object's opacity. */
  minPointOpacity: number;
  gradientStops: readonly LiveHeatmapGradientStop[];
}

export function assertValidLiveHeatmapConfig(config: LiveHeatmapConfig): void {
  const finitePositiveValues = [
    config.rasterScale,
    config.pointRadiusInMeters,
    config.heatRadiusMultiplier,
    config.maxValue,
  ];
  const stops = config.gradientStops;
  const stopsAreOrdered = stops.every(
    (stop, index) =>
      Number.isFinite(stop.value) &&
      stop.color.length > 0 &&
      (index === 0 || stop.value >= (stops.at(index - 1)?.value ?? 0))
  );

  if (
    finitePositiveValues.some((value) => !Number.isFinite(value) || value <= 0) ||
    !Number.isFinite(config.blurFactor) ||
    config.blurFactor < 0 ||
    !Number.isFinite(config.pointWeight) ||
    config.pointWeight < 0 ||
    !Number.isFinite(config.minPointOpacity) ||
    config.minPointOpacity < 0 ||
    config.minPointOpacity > 1 ||
    stops.length < 2 ||
    stops.at(0)?.value !== 0 ||
    stops.at(-1)?.value !== config.maxValue ||
    !stopsAreOrdered
  ) {
    throw new Error("Invalid live heatmap configuration");
  }
}

export function getCoolestLiveHeatmapColor(config: LiveHeatmapConfig): string {
  assertValidLiveHeatmapConfig(config);
  return config.gradientStops[0].color;
}

/**
 * Code-only heatmap tuning. The legacy normalization, blur, opacity, and color
 * rules are preserved; heat radius is relative to the physical person-dot size
 * so it remains proportional on differently sized maps.
 */
export const LIVE_HEATMAP_CONFIG: LiveHeatmapConfig = {
  // Emergency containment: one quarter of the previous raster pixel count.
  rasterScale: 0.125,
  pointRadiusInMeters: 0.2,
  heatRadiusMultiplier: 2,
  blurFactor: 2.2,
  pointWeight: 1,
  maxValue: 2.17,
  minPointOpacity: 0.05,
  gradientStops: [
    { value: 0, color: "#0000ff" },
    { value: 0.31, color: "#0000ff" },
    { value: 0.48, color: "#008000" },
    { value: 0.72, color: "#9acd32" },
    { value: 1.08, color: "#ffa500" },
    { value: 2.17, color: "#a52a2a" },
  ],
};
